import { useState } from "react"
import { Layout } from "../components/Layout"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { 
  MagnifyingGlassIcon,
  UsersIcon,
  UserGroupIcon,
  FilmIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline"

interface SearchResult {
  id: string
  type: "user" | "team" | "clip" | "game"
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
}

export function Search() {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return

    setIsSearching(true)
    setHasSearched(true)

    // Simulate search delay
    await new Promise(resolve => setTimeout(resolve, 500))

    // Mock search results
    const mockResults: SearchResult[] = [
      {
        id: "1",
        type: "user",
        title: "John Doe",
        description: "john@example.com • Admin",
        icon: UsersIcon,
      },
      {
        id: "2",
        type: "team",
        title: "Eagles FC",
        description: "12 members • Created 3 months ago",
        icon: UserGroupIcon,
      },
      {
        id: "3",
        type: "clip",
        title: "Goal vs Tigers",
        description: "Game on 2024-01-15 • 2:34 duration",
        icon: FilmIcon,
      },
    ]

    setResults(query.length > 2 ? mockResults : [])
    setIsSearching(false)
  }

  const getResultHref = (result: SearchResult): string => {
    switch (result.type) {
      case "user":
        return `/users/${result.id}`
      case "team":
        return `/teams/${result.id}`
      case "clip":
        return `/clips/${result.id}`
      case "game":
        return `/games/${result.id}`
      default:
        return "#"
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Search</h1>
          <p className="text-secondary">Search across users, teams, clips, and games</p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="max-w-2xl">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for users, teams, clips..."
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background text-primary placeholder:text-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              autoFocus
            />
            <button
              type="submit"
              disabled={isSearching || !query.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 rounded-md bg-primary text-contrast hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSearching ? "Searching..." : "Search"}
            </button>
          </div>
        </form>

        {/* Search History */}
        {!hasSearched && (
          <div className="max-w-2xl">
            <h3 className="text-sm font-medium text-secondary mb-3">Recent searches</h3>
            <div className="space-y-2">
              {["john doe", "eagles team", "goal clips"].map((term) => (
                <button
                  key={term}
                  onClick={() => {
                    setQuery(term)
                    handleSearch(new Event("submit") as any)
                  }}
                  className="flex items-center space-x-2 text-sm text-secondary hover:text-primary transition-colors"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  <span>{term}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {hasSearched && (
          <div className="max-w-2xl">
            {isSearching ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 rounded-lg border border-border bg-surface animate-pulse">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-md bg-secondary/20" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 bg-secondary/20 rounded" />
                        <div className="h-3 w-48 bg-secondary/20 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : results.length === 0 ? (
              <EmptyState
                icon={<DocumentIcon className="h-12 w-12" />}
                title="No results found"
                description={`No results found for "${query}". Try a different search term.`}
              />
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-secondary">
                  Found {results.length} results for &quot;{query}&quot;
                </p>
                {results.map((result) => (
                  <a
                    key={result.id}
                    href={getResultHref(result)}
                    className="block p-4 rounded-lg border border-border bg-surface hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                        <result.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium">{result.title}</h4>
                        <p className="text-sm text-secondary mt-1">{result.description}</p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}